// openEHR RM model — BMM package org.openehr.rm.common.generic
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { DV_MULTIMEDIA } from "../data_types/encapsulated.ts";
import type { DV_DATE_TIME } from "../data_types/quantity/date_time.ts";
import type { DV_CODED_TEXT, DV_TEXT } from "../data_types/text.ts";
import * as openehr_base from "../../base/mod.ts";
import { applyRmInit, type RmInit } from "../../base/apply_rm_init.ts";

/**
 * An entry in a revision history, corresponding to a version from a versioned container. Consists of \`AUDIT_DETAILS\` instances with revision identifier of the revision to which the \`AUDIT_DETAILS\` instance belongs.
 */
export class REVISION_HISTORY_ITEM {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Version identifier for this revision.
   */
  version_id?: openehr_base.OBJECT_VERSION_ID;
  /**
   * The audits for this revision; there will always be at least one commit audit (which may itself be an \`ATTESTATION\`), there may also be further attestations.
   */
  audits?: AUDIT_DETAILS[];
}

/**
 * The set of attributes required to document the committal of an information item to a repository.
 */
export class AUDIT_DETAILS {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * Internal storage for system_id
   * @protected
   */
  protected _system_id?: openehr_base.String;

  /**
   * Identifier of the logical EHR system where the change was committed. This is almost always owned by the organisation legally responsible for the EHR, and is distinct from any application, or any hosting infrastructure.
   */
  get system_id(): string | undefined {
    return this._system_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for system_id.
   * Use this to access openehr_base.String methods.
   */
  get $system_id(): openehr_base.String | undefined {
    return this._system_id;
  }

  /**
   * Sets system_id from either a primitive value or openehr_base.String wrapper.
   */
  set system_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._system_id = undefined;
    } else if (typeof val === "string") {
      this._system_id = openehr_base.String.from(val);
    } else {
      this._system_id = val;
    }
  }

  /**
   * Time of committal of the item.
   */
  time_committed?: DV_DATE_TIME;
  /**
   * Type of change. Coded using the openEHR Terminology  audit change type  group.
   */
  change_type?: DV_CODED_TEXT;
  /**
   * Reason for committal. This may be used to qualify the value in the \`_change_type_\` field. For example, if the change affects only the EHR directory, this field might be used to indicate 'Folder "episode 2018-02-16" added' or similar.
   */
  description?: DV_TEXT;
  /**
   * Identity and optional reference into identity management service, of user who committed the item.
   */
  committer?: PARTY_PROXY;
}

/**
 * Record an attestation of a party (the committer) to item(s) of record content. An attestation is an explicit signing by one healthcare agent of particular content for various particular purposes, including:
 *
 * * for authorisation of a controlled substance or procedure (e.g. sectioning of patient under mental health act);
 * * witnessing of content by senior clinical professional;
 * * indicating acknowledgement of content by intended recipient, e.g. GP who ordered a test result.
 */
export class ATTESTATION extends AUDIT_DETAILS {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Optional visual representation of content attested e.g. screen image.
   */
  attested_view?: DV_MULTIMEDIA;
  /**
   * Internal storage for proof
   * @protected
   */
  protected _proof?: openehr_base.String;

  /**
   * Proof of attestation.
   */
  get proof(): string | undefined {
    return this._proof?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for proof.
   * Use this to access openehr_base.String methods.
   */
  get $proof(): openehr_base.String | undefined {
    return this._proof;
  }

  /**
   * Sets proof from either a primitive value or openehr_base.String wrapper.
   */
  set proof(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._proof = undefined;
    } else if (typeof val === "string") {
      this._proof = openehr_base.String.from(val);
    } else {
      this._proof = val;
    }
  }

  /**
   * Items attested, expressed as fully qualified runtime paths to the items in question. Although not recommended, these may include fine-grained items which have been attested in some other system. Otherwise it is assumed to be for the entire VERSION with which it is associated.
   */
  items?: undefined;
  /**
   * Reason of this attestation. Optionally coded by the openEHR Terminology group  attestation reason ; includes values like  authorisation ,  witness  etc.
   */
  reason?: DV_TEXT;
  /**
   * Internal storage for is_pending
   * @protected
   */
  protected _is_pending?: openehr_base.Boolean;

  /**
   * True if this attestation is outstanding; False means it has been completed.
   */
  get is_pending(): boolean | undefined {
    return this._is_pending?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_pending.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_pending(): openehr_base.Boolean | undefined {
    return this._is_pending;
  }

  /**
   * Sets is_pending from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_pending(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_pending = undefined;
    } else if (typeof val === "boolean") {
      this._is_pending = openehr_base.Boolean.from(val);
    } else {
      this._is_pending = val;
    }
  }
}

/**
 * Model of a participation of a Party (any Actor or Role) in an activity.  Used to represent any participation of a Party in some activity, which is not  explicitly in the model, e.g. assisting nurse. Can be used to record past or  future participations.
 *
 * Should not be used in place of more permanent relationships between demographic entities.
 */
export class PARTICIPATION {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * The function of the Party in this participation (note that a given party might participate in more than one way in a particular activity). This attribute should be coded, but cannot be limited to the HL7v3:ParticipationFunction vocabulary, since it is too limited and hospital-oriented.
   */
  function?: DV_TEXT;
  /**
   * Optional field for recording the 'mode' of the performer / activity interaction, e.g. present, by telephone, by email etc.
   */
  mode?: DV_CODED_TEXT;
  /**
   * The id and possibly demographic system link of the party participating in the activity.
   */
  performer?: PARTY_PROXY;
  /**
   * The time interval during which the participation took place, if it is used in an observational context (i.e. recording facts about the past); or the intended time interval of the participation when used in future contexts, such as EHR Instructions.
   */
  time?: undefined;
}

/**
 * Abstract concept of a proxy description of a party, including an optional link to data for this party in a demographic or other identity management system. Sub- typed into \`PARTY_IDENTIFIED\` and \`PARTY_SELF\`.
 */
export abstract class PARTY_PROXY {
  /**
   * Optional reference to more detailed demographic or identification information for this party, in an external system.
   */
  external_ref?: openehr_base.PARTY_REF;
}

/**
 * Proxy data for an identified party other than the subject of the record, minimally consisting of human-readable identifier(s), such as name, formal (and possibly computable) identifiers such as NHS number, and an optional link to external data. There must be at least one of name, identifier or external_ref present.
 *
 * Used to describe parties where only identifiers may be known, and there is no entry at all in the demographic system (or even no demographic system). Typically for health care providers, e.g. name and provider number of an institution.
 *
 * Should not be used to include patient identifying information.
 */
export class PARTY_IDENTIFIED extends PARTY_PROXY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Optional human-readable name (in String form).
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * One or more formal identifiers (possibly computable).
   */
  identifiers?: undefined;
}

/**
 * Party proxy representing the subject of the record. Used to indicate that the party is the owner of the record. May or may not have \`_external_ref_\` set.
 */
export class PARTY_SELF extends PARTY_PROXY {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

}

/**
 * Proxy type for identifying a party and its relationship to the subject of the record. Use where the relationship between the party and the subject of the record must be known.
 */
export class PARTY_RELATED extends PARTY_IDENTIFIED {
  constructor(init?: RmInit) { super(); applyRmInit(this, init); }

  /**
   * Relationship of subject of this ENTRY to the subject of the record. May be coded. If it is the patient, coded as  self.
   */
  relationship?: DV_CODED_TEXT;
}

/**
 * Purpose Defines the notion of a revision history of audit items, each associated with the version for which that audit was committed. The list is in most-recent-first order.
 */
export class REVISION_HISTORY {
  constructor(init?: RmInit) { applyRmInit(this, init); }

  /**
   * The items in this history in most-recent-last order.
   */
  items?: REVISION_HISTORY_ITEM[];
  /**
   * The version id of the most recent item, as a String.
   * @returns Result value
   */
  most_recent_version(): openehr_base.String {
    if (!this.items || this.items.length === 0) {
      return openehr_base.String.from("");
    }
    // Return the version_id of the last item (most recent)
    const lastItem = this.items[this.items.length - 1];
    return openehr_base.String.from(lastItem.version_id?.value || "");
  }

  /**
   * The commit date/time of the most recent item, as a String.
   * @returns Result value
   */
  most_recent_version_time_committed(): openehr_base.String {
    if (!this.items || this.items.length === 0) {
      return openehr_base.String.from("");
    }
    // Get the last audit from the most recent item
    const lastItem = this.items[this.items.length - 1];
    if (!lastItem.audits || lastItem.audits.length === 0) {
      return openehr_base.String.from("");
    }
    return openehr_base.String.from(lastItem.audits[0].time_committed?.value || "");
  }
}
